/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// In-chat Rock Paper Scissors tile. Each player picks; the opponent's
// choice stays hidden until BOTH have moved, then both reveal + the same
// winner is shown. (True commit-reveal hardening is a follow-up; for a
// friendly chat game the hide-until-both-moved UX is the experience.)

import { Hand as HandIcon } from 'lucide-react-native';
import { useEffect, useRef } from 'react';
import { Animated, Easing, Pressable, Text, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import { useTileMaxWidth } from '../tileConstraint';
import { Banner, type GameColors, Header, WaitingRow } from './CoinFlipTile';
import {
  type RpsChoice,
  encodeRpsChoice,
  hasMoved,
  readRpsState,
} from './gameLogic';
import type { GameSession } from '../../sync/useGameSessions';

const EMOJI: Record<RpsChoice, string> = { rock: '✊', paper: '✋', scissors: '✌️' };
const CHOICES: ReadonlyArray<RpsChoice> = ['rock', 'paper', 'scissors'];

export interface RpsTileProps {
  readonly session: GameSession;
  readonly isCreator: boolean;
  readonly onBrand?: boolean | undefined;
  readonly onMove: (moveIdx: number, payload: string) => void;
  readonly selfName?: string | undefined;
  readonly opponentName?: string | undefined;
}

export function RpsTile({
  session,
  isCreator,
  onBrand,
  onMove,
  selfName,
  opponentName,
}: RpsTileProps): JSX.Element {
  const scheme = useColorScheme();
  const c: GameColors = tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'];
  // Thread side panel: cap the tile to the provided width.
  const tileMax = useTileMaxWidth();
  const accent = onBrand ? '#FFFFFF' : c.accent.indigo;
  const subText = onBrand ? 'rgba(255,255,255,0.75)' : c.text.tertiary;
  const state = readRpsState(session);

  const mySeatIdx: 0 | 1 = isCreator ? 0 : 1;
  const iPicked = hasMoved(session, mySeatIdx);
  const myChoice = isCreator ? state.creatorChoice : state.opponentChoice;
  const theirChoice = isCreator ? state.opponentChoice : state.creatorChoice;

  const pick = (choice: RpsChoice): void => onMove(mySeatIdx, encodeRpsChoice(choice));

  const iWon = state.resolved && state.winnerSeat === mySeatIdx;
  const winnerName =
    state.winnerSeat === mySeatIdx ? selfName ?? 'You' : opponentName ?? 'They';

  return (
    <View
      className={`w-[280px] max-w-full rounded-[18px] p-md gap-sm border ${
        onBrand
          ? 'bg-light-accent-brand dark:bg-[#537EFF] border-white/10'
          : 'bg-light-surface-secondary dark:bg-[#1A1D24] border-light-border-secondary dark:border-white/[0.06]'
      }`}
      style={tileMax !== null ? { maxWidth: tileMax } : undefined}
      accessibilityLabel="Rock paper scissors game"
    >
      <Header title="Rock · Paper · Scissors" Icon={HandIcon} colorAccent={accent} />

      {/* Reveal row */}
      <View className="flex-row items-center justify-around py-sm">
        <Hand
          choice={myChoice}
          revealed={true}
          label={selfName ?? 'You'}
          win={state.resolved && state.winnerSeat === mySeatIdx}
          colors={c}
        />
        <Text className="font-sans font-bold text-[13px] text-light-text-tertiary dark:text-[#B5BAC3]">
          vs
        </Text>
        <Hand
          choice={theirChoice}
          revealed={state.resolved}
          label={opponentName ?? 'Them'}
          win={state.resolved && state.winnerSeat === (isCreator ? 1 : 0)}
          colors={c}
        />
      </View>

      {state.resolved ? (
        <Banner
          text={state.winnerSeat === -1 ? "It's a draw!" : `${winnerName} won!`}
          win={iWon}
          colors={c}
        />
      ) : !iPicked ? (
        <View className="flex-row gap-sm">
          {CHOICES.map((choice) => (
            <Pressable
              key={choice}
              accessibilityRole="button"
              accessibilityLabel={choice}
              onPress={() => pick(choice)}
              className="flex-1 h-[44px] rounded-[10px] items-center justify-center bg-light-surface-tertiary dark:bg-white/[0.06] active:opacity-70"
              style={{ borderWidth: 1, borderColor: c.border.secondary }}
            >
              <Text style={{ fontSize: 22 }}>{EMOJI[choice]}</Text>
            </Pressable>
          ))}
        </View>
      ) : (
        <WaitingRow text="Waiting for opponent…" color={subText} />
      )}
    </View>
  );
}

function Hand({
  choice,
  revealed,
  label,
  win,
  colors,
}: {
  choice: RpsChoice | null;
  revealed: boolean;
  label: string;
  win: boolean;
  colors: GameColors;
}): JSX.Element {
  const show = revealed && choice !== null;
  // Pop-in when revealed.
  const pop = useRef(new Animated.Value(show ? 1 : 0)).current;
  useEffect(() => {
    if (show) {
      pop.setValue(0.4);
      Animated.timing(pop, {
        toValue: 1,
        duration: 320,
        easing: Easing.out(Easing.back(2)),
        useNativeDriver: true,
      }).start();
    }
  }, [show, choice, pop]);

  return (
    <View className="items-center gap-1">
      <Animated.View
        style={{
          width: 56,
          height: 56,
          borderRadius: 28,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: win ? '#3CCB4F22' : colors.surface.tertiary,
          borderWidth: 2,
          borderColor: win ? '#3CCB4F' : colors.border.secondary,
          transform: [{ scale: show ? pop : 1 }],
        }}
      >
        <Text style={{ fontSize: 26 }}>
          {show ? EMOJI[choice!] : choice !== null ? '✓' : '…'}
        </Text>
      </Animated.View>
      <Text
        numberOfLines={1}
        className="font-sans text-[11px] leading-4 max-w-[80px] text-light-text-secondary dark:text-[#B5BAC3]"
      >
        {label}
      </Text>
    </View>
  );
}
